import { Gift } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { dateLocale } from '../../i18n/locale';
import { giftCardApi, type MyGiftCardRow } from '../../services/giftCardApi';

function dollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** A single card row: amount, recipient, created-at, code (toggle), resend button. */
function CardRow({
  card,
  onResend,
  t,
  locale,
}: {
  card: MyGiftCardRow;
  onResend: (id: string) => Promise<void>;
  t: (k: string) => string;
  locale: string;
}) {
  const [showCode, setShowCode] = useState(false);
  // After clicking Resend we flip a transient "Email resent" label. We do
  // this regardless of whether the backend actually sent the email (rate
  // limit may have silently skipped it) so the user can't infer status.
  const [justResent, setJustResent] = useState(false);
  const [resending, setResending] = useState(false);

  const handleResend = async () => {
    setResending(true);
    try {
      await onResend(card.id);
      setJustResent(true);
      setTimeout(() => setJustResent(false), 4000);
    } finally {
      setResending(false);
    }
  };

  const recipientLabel = card.isGift
    ? card.recipientName
      ? `${card.recipientName} <${card.recipientEmail}>`
      : (card.recipientEmail ?? '')
    : t('myCards.selfRecipient');

  return (
    <li className="bg-white rounded-lg shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-lg font-semibold text-gray-900">${dollars(card.faceValue)}</p>
        <p className="text-sm text-gray-600 truncate">{recipientLabel}</p>
        <p className="text-xs text-gray-400">
          {new Date(card.createdAt).toLocaleDateString(dateLocale(locale))}
        </p>
        {showCode && (
          <p className="mt-2 font-mono text-sm text-charcoal tracking-wider break-all">
            {card.code}
          </p>
        )}
      </div>
      <div className="flex gap-2 sm:flex-col sm:items-end">
        <button
          type="button"
          onClick={() => setShowCode((s) => !s)}
          className="text-sm px-3 py-1 rounded-md border border-gray-300 text-gray-700 hover:border-primary hover:text-primary transition-colors"
        >
          {showCode ? t('myCards.hideCode') : t('myCards.showCode')}
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="text-sm px-3 py-1 rounded-md bg-primary text-white hover:bg-primary-dark disabled:opacity-60 transition-colors"
        >
          {justResent ? t('myCards.resent') : t('myCards.resend')}
        </button>
      </div>
    </li>
  );
}

/** Compact reuse of the homepage banner styling for the empty state. */
function EmptyState({ t }: { t: (k: string) => string }) {
  return (
    <div className="flex justify-center">
      <Link
        to="/gift-cards"
        className="inline-flex flex-wrap items-center justify-center gap-4 sm:gap-6 max-w-2xl bg-white border border-soft-gray rounded-2xl px-4 sm:px-6 py-3 shadow-brand hover:border-primary hover:shadow-md transition-all"
      >
        <span
          className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary/10"
          aria-hidden="true"
        >
          <Gift className="w-5 h-5 text-primary" strokeWidth={2} />
        </span>
        <p className="text-charcoal font-medium">{t('homeBanner.headline')}</p>
        <span className="inline-flex items-center px-4 py-2 rounded-lg bg-primary text-white font-semibold whitespace-nowrap text-sm">
          {t('homeBanner.cta')}
        </span>
      </Link>
    </div>
  );
}

export default function MyGiftCardsTab() {
  const { t, i18n } = useTranslation('giftCard');
  const { isAuthenticated, getToken } = useAuth();
  const [cards, setCards] = useState<MyGiftCardRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const token = await getToken();
        if (!token) throw new Error('No access token');
        setCards(await giftCardApi.listMyCards(token));
      } catch (err) {
        setError(err instanceof Error ? err.message : t('myCards.loadError'));
      }
    })();
  }, [isAuthenticated, getToken, t]);

  const handleResend = async (id: string) => {
    const token = await getToken();
    if (!token) return;
    await giftCardApi.resendOwnCard(id, token);
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
    );
  }

  if (cards === null) {
    return <p className="text-gray-500">{t('myCards.loading')}</p>;
  }

  if (cards.length === 0) {
    return <EmptyState t={t} />;
  }

  return (
    <ul className="space-y-3">
      {cards.map((card) => (
        <CardRow key={card.id} card={card} onResend={handleResend} t={t} locale={i18n.language} />
      ))}
    </ul>
  );
}
